import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Heart, CheckCircle2, Clock, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Grundpflege() {
  return (
    <>
      <Helmet>
        <title>Grundpflege Itzehoe | Körperpflege zuhause | Phönix Pflegeteam</title>
        <meta name="description" content="Grundpflege in Itzehoe ✓ Körperpflege ✓ Ankleiden ✓ Mobilität ✓ Ernährung ✓ 45€/h (Pflegekasse übernimmt) ✓ Würdevolle Unterstützung im eigenen Zuhause" />
        <link rel="canonical" href="https://pflegeteam-itzehoe.de/grundpflege" />
      </Helmet>

      <main id="main-content">
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
                <Heart className="w-4 h-4 text-[#1A52D8]" />
                <span className="text-sm font-medium text-[#1A52D8]">Grundpflege</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Grundpflege in Itzehoe - Würdevoll im eigenen Zuhause
              </h1>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#1A52D8]/20 mb-8 direct-answer">
                <p className="text-lg text-[#475569] leading-relaxed mb-4">
                  <strong className="text-[#1A52D8]">Grundpflege bedeutet:</strong> Wir unterstützen Sie bei der täglichen Körperpflege, beim An- und Ausziehen, bei der Nahrungsaufnahme und Mobilität. Immer respektvoll, immer in Ihrem Tempo.
                </p>
                <p className="text-lg text-[#475569] leading-relaxed">
                  <strong>Kosten:</strong> 45€/Stunde. <strong>Pflegekasse übernimmt:</strong> 100% ab Pflegegrad 2 (bis zu 2.200€/Monat).
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+4915146173685">
                  <Button className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 px-8 rounded-full text-lg font-semibold">
                    <Phone className="w-5 h-5" />
                    Jetzt beraten lassen
                  </Button>
                </a>
                <Link to="/kosten">
                  <Button className="bg-white text-[#1A52D8] border-2 border-[#1A52D8]/20 hover:border-[#1A52D8] h-14 px-8 rounded-full text-lg font-semibold">
                    Kosten ansehen
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8">
              Wenn Angehörige überfordert sind
            </h2>
            
            <div className="prose prose-lg max-w-none text-[#475569] mb-8">
              <p className="mb-4">
                "Mama schafft das Duschen nicht mehr allein. Papa kann sich nicht mehr anziehen. 
Und ich als Tochter... ich weiß nicht, wie ich das machen soll."
              </p>
              <p className="mb-4">
                Diese Situation kennen wir aus Itzehoe hundertfach. Die eigenen Eltern zu pflegen ist schwer - körperlich und emotional. Es ist okay, sich Hilfe zu holen.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <h3 className="font-bold text-[#1e293b] mb-3">Was wir bei Grundpflege machen:</h3>
                <ul className="space-y-2 text-[#64748b]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Körperpflege (Waschen, Duschen, Haare waschen)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>An- und Auskleiden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Hilfe beim Essen und Trinken</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Mobilisation (Aufstehen, Lagern, Gehen)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#23D3D3] mt-0.5 flex-shrink-0" />
                    <span>Toilettengang / Inkontinenzversorgung</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#E8F8F8] rounded-xl p-6">
                <h3 className="font-bold text-[#1A52D8] mb-3">Warum Grundpflege so wichtig ist:</h3>
                <p className="text-[#475569] text-sm leading-relaxed mb-3">
                  Körperpflege ist Würde. Wer sich nicht mehr selbst waschen kann, verliert oft das Selbstwertgefühl. Wir helfen so, dass Sie Ihre Würde behalten - respektvoll, diskret, in Ihrem Tempo.
                </p>
                <p className="text-[#475569] text-sm leading-relaxed">
                  In Itzehoe betreuen wir Menschen von 65 bis 95 Jahren. Jeder hat andere Bedürfnisse. Manche brauchen nur Hilfe beim Duschen, andere bei allem. Wir passen uns an - nicht Sie sich uns.
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-[#1e293b] mb-6 mt-12">
              Typische Situationen in Itzehoe
            </h2>

            <div className="space-y-6">
              <div className="border-l-4 border-[#1A52D8] pl-6 py-2">
                <h4 className="font-bold text-[#1e293b] mb-2">"Mama traut sich nicht mehr zu duschen"</h4>
                <p className="text-[#64748b]">Sturzangst ist bei Senioren in Itzehoe sehr verbreitet. Unsere Pflegekräfte sichern Sie beim Duschen ab, installieren wenn nötig Haltegriffe und sorgen dafür, dass Sie sich wieder trauen. Viele unserer Klienten genießen das Duschen wieder - weil sie sich sicher fühlen.</p>
              </div>

              <div className="border-l-4 border-[#23D3D3] pl-6 py-2">
                <h4 className="font-bold text-[#1e293b] mb-2">"Papa schafft die Treppen nicht mehr"</h4>
                <p className="text-[#64748b]">Viele Häuser in Itzehoe haben Treppen - Bad im OG, Schlafzimmer oben. Wir helfen beim sicheren Treppensteigen oder organisieren temporäre Lösungen (Waschschüssel unten, Pflegebett). Umzug ins Heim ist oft nicht nötig!</p>
              </div>

              <div className="border-l-4 border-[#1A52D8] pl-6 py-2">
                <h4 className="font-bold text-[#1e293b] mb-2">"Ich als Tochter schaffe das nicht mehr"</h4>
                <p className="text-[#64748b]">Sie arbeiten Vollzeit in Hamburg, Ihre Mutter wohnt in Itzehoe. Jeden Tag anfahren ist unmöglich. Wir übernehmen die morgendliche Pflege - Sie kommen abends zum Kaffee. So geht es vielen Angehörigen. Sie sind nicht allein.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              Grundpflege in Ihrer Nähe
            </h2>
            <p className="text-lg text-[#64748b] mb-8">
              Wir bieten Grundpflege in allen Orten des Kreis Steinburg:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Itzehoe', 'Glückstadt', 'Wilster', 'Kellinghusen', 'Krempe', 'Hohenlockstedt', 'Dägeling', 'Heiligenstedten'].map((city) => (
                <Link key={city} to={`/ambulante-pflege/${city.toLowerCase().replace('ü', 'ue').replace('ä', 'ae')}`} className="px-4 py-2 bg-white rounded-full text-sm text-[#1A52D8] border border-[#1A52D8]/20 hover:bg-[#1A52D8] hover:text-white transition-all">
                  {city}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white">
          <div className="container-custom max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Brauchen Sie Hilfe bei der Grundpflege?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Kostenlose Beratung, unverbindliches Erstgespräch bei Ihnen zuhause. Wir kommen in ganz Itzehoe und Umgebung.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt Anrufen: 0151 46173685
              </Button>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
